import { ApiError, NetworkError, parseRetryAfter, type ProblemDetails } from './problem';
import { TokenStore } from './tokenStore';
import type {
  ConversationListQuery, TenantModuleKind, TenantModuleResponse,
  AllergenOptionResponse, AllergenResponse, CreateAllergenRequest, UnmatchedAllergenResponse, UpdateAllergenRequest,
  AcademicYearResponse, CalendarSpanRequest, CloseAcademicYearRequest, CloseAcademicYearResponse,
  RolloverPreviewResponse, TermResponse,
  AbsenceNoticeResponse, AddGuardianRequest, AdminDashboardResponse, AttendanceBoardResponse,
  AttendanceStatus, AttendanceSummaryResponse, AnnouncementResponse, AuditEntryResponse,
  AuthorizedPickupResponse, CalendarEventResponse, CareEntryKind, CatalogItemResponse, CatalogType, CollectorsResponse,
  ChildAccountResponse, ChildDetailResponse, ChildPhotoResponse, ChildHealthResponse, ChildStatus, ChildSummaryResponse,
  ClassDaySummaryResponse, ClassResponse, ClassRole, ComplaintAssigneeResponse, ComplaintCategory,
  ComplaintEventResponse, ComplaintResponse, ComplaintSeverity, ComplaintStatus, ConsentResponse,
  ConsentGapResponse, ConsentType, ContactResponse, RegistrationGapResponse,
  ConversationEventResponse, ConversationResponse, ShareableMediaResponse, UnreadCountResponse,
  CreateAnnouncementRequest, CreateCalendarEventRequest, CreateChildRequest,
  CloseIncidentRequest, DiaperLogChoice,
  CreateClassRequest, CreateComplaintRequest, CreateFeePlanRequest, CreateIncidentRequest,
  CreateTenantRequest,
  CurrentSessionResponse, DailyReportResponse, DiscountType, EnrollmentResponse, FeePlanResponse,
  GenerateInvoicesResponse, GuardianLinkResponse, GuardianLinkedResponse, UpdateGuardianLinkRequest,
  ImportResultResponse, ImportRow, IncidentResponse, IncomeReportResponse, InvitationIssuedResponse,
  AttendanceReportResponse, DailyCareRecordersResponse, DailyCareReportResponse, ReportMonthQuery, RosterRow,
  InviteStaffRequest, InvoiceResponse, InvoiceStatus, LoginResponse, MedicationResponse,
  MembershipStatus, MembershipSummary, MessageResponse, MessageSearchHit, MyChildResponse,
  NotificationPreferenceResponse, NotificationResponse, OccasionResponse, OverdueListResponse,
  PagedResponse, PaymentResponse, PermissionOptionResponse, PermissionOverrideResponse, PickupRestrictionResponse,
  ChildExportResponse, ExportRecipientResponse, PhotoHandoverResponse, LegalHoldResponse,
  PhotoGalleryResponse, PhotoResponse, PhotoStatus, PhotoUploadMetadata, PhotoVisibilityMode,
  PlatformAnnouncementResponse, PlatformAuditEntryResponse, PlatformDashboardResponse,
  PlatformRoleResponse, PlatformUserResponse, ProrationPolicy, ProvisionedTenantResponse,
  AnnouncementDeliveryResponse, AuditEntityOptionResponse, CreatePlatformAnnouncementRequest,
  InvitePlatformUserRequest, InvitedPlatformUserResponse, RecordCareEntryRequest,
  RecordCareEntryResponse, RecordPaymentRequest, SessionSummary, SetPreferenceRequest,
  SetYearFeeRequest, YearFeeListResponse, YearFeeStatus, YearFeeSummary,
  SettingResponse, StaffResponse, TenantBrandingResponse, TenantLogoResponse, TenantProfileResponse,
  TenantSettingsResponse, TokenResponse, ClientLimits, UpdateSettingsResponse,
  UpdateTenantProfileRequest, UpdateTenantRequest,
  AccountResponse, CentreRecoveryRequestResponse, ClientAppName, InvitationCodeResponse, InvitationPreviewResponse,
  IdentityResponse, NumberReleasedResponse, PlatformRecoveryRequestResponse, RecoveryLinkResponse,
  RecoveryRequestResponse, StepUpResponse,
  PasswordResetRequestedResponse, PhoneChangedResponse, PhoneChangeRequestedResponse,
  PasskeyRegisteredResponse, PasskeySummary,
  LegalAcceptance, LegalDocumentName, LegalText,
  BackupListResponse, BackupOperationResponse, PushConfigResponse, RegisterDeviceRequest,
} from './types';
import type { CreationOptionsJson, RequestOptionsJson } from '../auth/webauthn';

/**
 * Which app a stored-session namespace belongs to, so a link the API sends opens where the person
 * started. Unknown keys (a test harness, say) fall back to the guardian app.
 */
export function clientAppName(appKey: string): ClientAppName {
  switch (appKey) {
    case 'staff': return 'staff';
    case 'admin-console': return 'admin';
    case 'platform-console': return 'platform';
    default: return 'guardian';
  }
}

export interface ClientOptions {
  /** Origin of the API, e.g. `http://localhost:5001`. No trailing slash. */
  baseUrl: string;
  /** Namespace for this application's stored session. See TokenStore. */
  appKey: string;
  /** Called when the session ends and cannot be recovered, so the shell can route to sign-in. */
  onSessionEnded?: () => void;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Send without a bearer token, and never try to refresh. Sign-in and branding lookups use this. */
  anonymous?: boolean;
  /** Bearer token to use instead of the stored one — the short-lived membership-selection token. */
  bearer?: string;
  /**
   * Send and keep cookies. Only the passkey ceremonies need it: the challenge lives in a short-lived
   * http-only cookie between their two calls. Everything else is bearer-only and leaves cookies out.
   */
  withCookies?: boolean;
  /** A step-up token from `auth.stepUp`, for the one change that asks for the password again (Q25). */
  stepUp?: string;
  /** What the call accepts back; JSON unless it asks for a file. */
  accept?: string;
  signal?: AbortSignal;
}

const API_VERSION = 'v1';

/**
 * The legal versions an invitation is accepted with (Q67), under the names the invitation routes give them:
 * `legalLanguage` there, `language` on `/legal/acceptances`.
 */
function invitationLegalFields(legal: LegalAcceptance | null) {
  return { privacyVersion: legal?.privacyVersion, termsVersion: legal?.termsVersion, legalLanguage: legal?.language };
}

/**
 * The Daghfal API client.
 *
 * One instance per application, created at start-up and handed to React through `ApiProvider`.
 *
 * It owns three things its callers should not have to think about: the bearer token, the single-flight
 * refresh when that token has expired, and turning a non-2xx response into an `ApiError` carrying the
 * API's own Arabic problem text.
 */
export class DaghfalClient {
  readonly tokens: TokenStore;
  /** Which of the four apps this client belongs to. */
  readonly appName: ClientAppName;
  private readonly baseUrl: string;
  private readonly onSessionEnded: (() => void) | undefined;

  /**
   * A refresh in progress. Concurrent requests that all see a 401 await this one promise instead of each
   * presenting the same refresh token — which the API treats as a leak and answers by revoking the chain.
   */
  private refreshing: Promise<string | null> | null = null;

  /** The Web Lock every copy of this app on the device takes to refresh, so only one presents a refresh token. */
  private readonly refreshLock: string;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.tokens = new TokenStore(options.appKey);
    this.refreshLock = `daghfal.${options.appKey}.refresh`;
    this.appName = clientAppName(options.appKey);
    this.onSessionEnded = options.onSessionEnded;
  }

  /**
   * A media link the API handed out, made absolute against the API itself.
   *
   * Signed links come back as `/media?key=…` wherever the API has no public origin configured, and a
   * browser resolves a relative link against the page's origin, which is the app's, not the API's. So
   * every `<img>`, `<video>` and `<audio>` goes through this. A link that is already absolute is returned
   * as it came.
   */
  mediaUrl(url: string | null | undefined): string | undefined {
    return url ? new URL(url, `${this.baseUrl}/`).toString() : undefined;
  }

  // ---- Transport ---------------------------------------------------------------------------------------

  private url(path: string, query?: RequestOptions['query']): string {
    const url = new URL(`${this.baseUrl}/api/${API_VERSION}${path}`);
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== null && value !== undefined && value !== '') url.searchParams.set(key, String(value));
    }
    return url.toString();
  }

  private async send(path: string, options: RequestOptions, bearer: string | null): Promise<Response> {
    const headers: Record<string, string> = { Accept: options.accept ?? 'application/json', 'X-Api-Version': '1.0' };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
    if (options.stepUp) headers['X-Step-Up'] = options.stepUp;

    const init: RequestInit = { method: options.method ?? 'GET', headers };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    if (options.signal) init.signal = options.signal;
    if (options.withCookies) init.credentials = 'include';

    try {
      return await fetch(this.url(path, options.query), init);
    } catch (cause) {
      // `fetch` rejects only when the request never completed. An abort is the caller's own doing and is
      // rethrown as-is so React Query can tell it apart from a genuine connectivity failure.
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
      throw new NetworkError(cause);
    }
  }

  /**
   * Sends with the session's bearer, refreshing once on a 401, and throws the API's problem for anything
   * that is not a success. The one place the refresh is asked for, so every call keeps to its single flight.
   */
  private async authorised(
    attempt: (bearer: string | null) => Promise<Response>,
    options: Pick<RequestOptions, 'anonymous' | 'bearer'> = {},
  ): Promise<Response> {
    const bearer = options.anonymous ? null : (options.bearer ?? this.tokens.read()?.accessToken ?? null);

    let response = await attempt(bearer);

    // One retry, and only for a stored session: a caller that passed an explicit bearer is using a
    // single-purpose token that cannot be refreshed.
    if (response.status === 401 && !options.anonymous && !options.bearer) {
      const refreshed = await this.refresh(bearer);
      if (refreshed) response = await attempt(refreshed);
    }

    if (!response.ok) {
      throw new ApiError(response.status, await readProblem(response), parseRetryAfter(response.headers.get('Retry-After')));
    }
    return response;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.authorised((bearer) => this.send(path, options, bearer), options);

    if (response.status === 204 || response.status === 205) return undefined as T;
    return readJson<T>(response);
  }

  /**
   * Fetches a file the API generates, a printed PDF, refreshing once on a 401 exactly as `request` does.
   *
   * The bearer token cannot go on a link, so the file is fetched here and handed to the caller to show.
   */
  async file(path: string, accept: string, query?: RequestOptions['query']): Promise<Blob> {
    const response = await this.authorised((bearer) => this.send(path, { accept, query }, bearer));
    return response.blob();
  }

  /**
   * Sends a multipart body, refreshing once on a 401 exactly as `request` does.
   *
   * Separate from `request` because the two differ in one important way: `Content-Type` must be left to
   * the browser, which appends the boundary. Setting it by hand produces a body the server cannot parse.
   */
  private async sendForm<T>(path: string, form: FormData, method: 'POST' | 'PUT' = 'POST'): Promise<T> {
    const attempt = async (bearer: string | null): Promise<Response> => {
      const headers: Record<string, string> = { Accept: 'application/json', 'X-Api-Version': '1.0' };
      if (bearer) headers['Authorization'] = `Bearer ${bearer}`;

      try {
        return await fetch(this.url(path), { method, headers, body: form });
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
        throw new NetworkError(cause);
      }
    };

    return readJson<T>(await this.authorised(attempt));
  }

  // ---- Session -----------------------------------------------------------------------------------------

  /**
   * Exchanges the stored refresh token for a new pair, at most once at a time.
   *
   * Returns the new access token, or null when the session is over — in which case the stored session is
   * cleared and `onSessionEnded` fires, so a stale token cannot sit in storage looking valid.
   */
  private refresh(refused: string | null): Promise<string | null> {
    if (this.refreshing) return this.refreshing;

    this.refreshing = (async () => {
      try {
        return await this.underRefreshLock(() => this.refreshOnce(refused));
      } finally {
        this.refreshing = null;
      }
    })();

    return this.refreshing;
  }

  /**
   * Runs `work` holding the device-wide refresh lock where the browser has Web Locks, so two copies of this app (two
   * tabs, the window a push opened, the page the browser restored) never present the same refresh token. A second
   * presentation is what a stolen token looks like, and the API answers it by ending the session (2026-10-15).
   */
  private async underRefreshLock(work: () => Promise<string | null>): Promise<string | null> {
    const locks = globalThis.navigator?.locks;
    return locks ? await locks.request(this.refreshLock, work) : await work();
  }

  /**
   * One refresh, after a request sent with `refused` came back 401. Storage is read afresh first: when another copy
   * has refreshed since, its token is used and nothing is presented.
   */
  private async refreshOnce(refused: string | null): Promise<string | null> {
    const stored = this.tokens.reload();
    if (!stored?.refreshToken) {
      this.endSession();
      return null;
    }
    if (refused !== null && stored.accessToken !== refused) return stored.accessToken;

    try {
      const pair = await this.request<TokenResponse>('/auth/refresh', {
        method: 'POST',
        body: { refreshToken: stored.refreshToken },
        anonymous: true,
      });
      this.tokens.write(pair, stored.membership);
      return pair.accessToken;
    } catch (error) {
      // A network failure means we simply do not know, and keeps the session.
      if (!(error instanceof ApiError)) return null;

      // Without Web Locks another copy can refresh between our read and our request, and ours is then refused as
      // spent: its tokens are in storage, and the session is not over.
      const now = this.tokens.reload();
      if (now?.refreshToken && now.refreshToken !== stored.refreshToken) return now.accessToken;

      // A refusal of the token we hold means the chain is spent or revoked.
      this.endSession();
      return null;
    }
  }

  private endSession(): void {
    this.tokens.clear();
    this.onSessionEnded?.();
  }

  // ---- Authentication ----------------------------------------------------------------------------------

  /** Keeps the session a sign-in produced; one still waiting on a membership choice carries no tokens. */
  private adoptSession(result: LoginResponse): LoginResponse {
    if (result.tokens) this.tokens.write(result.tokens, result.membership);
    return result;
  }

  /**
   * Signs in. Where the user holds one membership the session is ready; where they hold several the
   * response carries a selection token and the memberships to choose between, and nothing is reachable
   * until `selectMembership` is called (SRS §4.1.2, D-2).
   */
  async login(phone: string, password: string): Promise<LoginResponse> {
    return this.adoptSession(await this.request<LoginResponse>('/auth/login', {
      method: 'POST',
      body: { phone, password },
      anonymous: true,
    }));
  }

  async selectMembership(membershipId: number, selectionToken: string): Promise<LoginResponse> {
    return this.adoptSession(await this.request<LoginResponse>('/auth/select-membership', {
      method: 'POST',
      body: { membershipId },
      bearer: selectionToken,
    }));
  }

  /**
   * Accepts an invitation. `phoneCode` is the six-digit code texted by `invitations.sendCode`, needed every
   * time (decision 2026-09-23 #9, Q24). For a number that already has an account, `password` is that
   * account's own, or `passkeyCredentialJson` is an assertion by one of its passkeys, made for a challenge
   * from `passkeys.assertionOptions` in this same browser.
   */
  async acceptInvitation(
    token: string,
    password: string | null,
    phoneCode: string | null = null,
    passkeyCredentialJson: string | null = null,
    legal: LegalAcceptance | null = null,
  ): Promise<LoginResponse> {
    return this.adoptSession(await this.request<LoginResponse>('/auth/accept-invitation', {
      method: 'POST',
      body: {
        token, password, phoneCode, passkeyCredentialJson, ...invitationLegalFields(legal),
      },
      anonymous: true,
      // The passkey's challenge cookie has to travel with it.
      withCookies: passkeyCredentialJson !== null,
    }));
  }

  /**
   * Says the account the invited number belongs to is not the holder's, and accepts the invitation as a new
   * account with `newPassword` (Q25). Allowed only where the preview's `releaseOption` is `self`. The old
   * account keeps its history; nobody can sign in to it again.
   */
  async releaseInvitedNumber(
    token: string, phoneCode: string, newPassword: string, legal: LegalAcceptance | null = null,
  ): Promise<LoginResponse> {
    return this.adoptSession(await this.request<LoginResponse>('/auth/invitation/release', {
      method: 'POST',
      body: {
        token, phoneCode, newPassword, ...invitationLegalFields(legal),
      },
      anonymous: true,
    }));
  }

  /**
   * Signs in with a passkey assertion, made for a challenge from `passkeys.assertionOptions`. Answers as
   * `login` does: a session, or the memberships to choose between.
   */
  async signInWithPasskey(credentialJson: string): Promise<LoginResponse> {
    return this.adoptSession(await this.request<LoginResponse>('/auth/passkeys/assertion', {
      method: 'POST',
      body: { credentialJson },
      anonymous: true,
      withCookies: true,
    }));
  }

  // ---- Passkeys ------------------------------------------------------------------------------------------

  /**
   * Each ceremony is two calls with the challenge held in a cookie between them, so every call that starts
   * or finishes one is sent with cookies (`withCookies`). Listing and removing are ordinary bearer calls.
   */
  passkeys = {
    /** The signed-in person's passkeys. */
    list: (): Promise<PasskeySummary[]> => this.request<PasskeySummary[]>('/auth/passkeys'),

    /** Begins registering a passkey for the signed-in person: the options for `createPasskey`. */
    /** Begins registering a passkey. Needs a step-up token: the password, proven again minutes ago (Q25). */
    attestationOptions: (stepUp: string): Promise<CreationOptionsJson> =>
      this.request<CreationOptionsJson>('/auth/passkeys/attestation/options', { method: 'POST', withCookies: true, stepUp }),

    /** Proves the password again, for a change that asks for it; the token lasts a few minutes. */
    stepUp: (password: string): Promise<StepUpResponse> =>
      this.request<StepUpResponse>('/auth/step-up', { method: 'POST', body: { password } }),

    /** Finishes registering it. `deviceName` is what the list will call it. */
    completeAttestation: (credentialJson: string, deviceName: string | null): Promise<PasskeyRegisteredResponse> =>
      this.request<PasskeyRegisteredResponse>('/auth/passkeys/attestation', {
        method: 'POST', body: { credentialJson, deviceName }, withCookies: true,
      }),

    /**
     * Begins a passkey sign-in: the options for `getPasskey`. With nothing, the device offers whichever
     * passkeys it holds for this site; with a number, only that account's; with an invitation's token, only
     * the invited account's, whose number the invitation screen has only masked.
     */
    assertionOptions: (
      who: { phone?: string | null; invitationToken?: string | null } = {},
    ): Promise<RequestOptionsJson> =>
      this.request<RequestOptionsJson>('/auth/passkeys/assertion/options', {
        method: 'POST',
        body: { phone: who.phone ?? null, invitationToken: who.invitationToken ?? null },
        anonymous: true,
        withCookies: true,
      }),

    remove: (credentialId: string): Promise<void> =>
      this.request<void>(`/auth/passkeys/${encodeURIComponent(credentialId)}`, { method: 'DELETE' }),
  };

  async logout(): Promise<void> {
    const refreshToken = this.tokens.read()?.refreshToken;
    try {
      if (refreshToken) {
        await this.request<void>('/auth/logout', { method: 'POST', body: { refreshToken }, anonymous: true });
      }
    } finally {
      // The local session goes regardless. A user who taps "sign out" on a flaky connection must not be
      // left signed in on the device.
      this.endSession();
    }
  }

  me(signal?: AbortSignal): Promise<CurrentSessionResponse> {
    return this.request<CurrentSessionResponse>('/auth/me', signal ? { signal } : {});
  }

  listSessions(): Promise<SessionSummary[]> {
    return this.request<SessionSummary[]>('/auth/sessions');
  }

  revokeAllSessions(): Promise<void> {
    return this.request<void>('/auth/sessions', { method: 'DELETE' });
  }

  // ---- Legal texts (Q67) ---------------------------------------------------------------------------------

  legal = {
    /** The privacy notice or the terms, anonymous: an invitee reads both before they have an account. */
    text: (document: LegalDocumentName, language: string): Promise<LegalText> =>
      this.request<LegalText>(`/legal/${document}?lang=${encodeURIComponent(language)}`, { anonymous: true }),
    /** Accepts the versions in force, as the page showed them. */
    accept: (acceptance: LegalAcceptance): Promise<void> =>
      this.request<void>('/legal/acceptances', { method: 'POST', body: acceptance }),
  };

  // ---- Invitations, before accepting (anonymous) ---------------------------------------------------------

  invitations = {
    /** What an invitation is for, and whether accepting it needs a password and a phone code. */
    preview: (token: string): Promise<InvitationPreviewResponse> =>
      this.request<InvitationPreviewResponse>('/auth/invitation/preview', {
        method: 'POST', body: { token }, anonymous: true,
      }),

    /** Texts the code that confirms the invitee's phone. */
    sendCode: (token: string): Promise<InvitationCodeResponse> =>
      this.request<InvitationCodeResponse>('/auth/invitation/code', {
        method: 'POST', body: { token }, anonymous: true,
      }),
  };

  // ---- The caller's own account --------------------------------------------------------------------------

  account = {
    get: (): Promise<AccountResponse> => this.request<AccountResponse>('/account'),

    /** Sets or replaces the caller's photo. Send what `toProfilePhoto` made: square, at most `media.profilePhotoMaximumEdge`. */
    setPhoto: (photo: Blob): Promise<AccountResponse> =>
      this.sendForm<AccountResponse>('/account/photo', fileForm(photo, 'photo.jpg'), 'PUT'),

    removePhoto: (): Promise<AccountResponse> =>
      this.request<AccountResponse>('/account/photo', { method: 'DELETE' }),

    /** Sets, changes or (with an empty address) removes the email on file. */
    setEmail: (email: string | null, currentPassword: string): Promise<AccountResponse> =>
      this.request<AccountResponse>('/account/email', { method: 'PUT', body: { email, currentPassword } }),

    /**
     * Changes the password. The API ends every session and hands this device a new one, which is stored
     * here straight away so the person stays signed in (decision 2026-09-24 #5).
     */
    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
      const pair = await this.request<TokenResponse>('/account/password', {
        method: 'POST', body: { currentPassword, newPassword },
      });
      this.tokens.write(pair, this.tokens.read()?.membership ?? null);
    },

    /** Sends a confirmation link to the new number. The answer is the same whether or not it can be used. */
    requestPhoneChange: (newPhone: string, currentPassword: string): Promise<PhoneChangeRequestedResponse> =>
      this.request<PhoneChangeRequestedResponse>('/account/phone-change', {
        method: 'POST', body: { newPhone, currentPassword, app: this.appName },
      }),

    cancelPhoneChange: (): Promise<void> =>
      this.request<void>('/account/phone-change', { method: 'DELETE' }),

    /** Opens a phone-change link. Every session ends, this one included. */
    confirmPhoneChange: (token: string): Promise<PhoneChangedResponse> =>
      this.request<PhoneChangedResponse>('/auth/phone-change/confirm', {
        method: 'POST', body: { token }, anonymous: true,
      }),

    /**
     * Texts a code to the caller's own number, for someone signed in (with a passkey, say) who has forgotten the
     * password. An administrator or platform member in a password session is refused (`passkey-session-required`).
     */
    sendSessionResetCode: (): Promise<PasswordResetRequestedResponse> =>
      this.request<PasswordResetRequestedResponse>('/account/password-reset/code', { method: 'POST', body: {} }),

    /**
     * Sets a new password with the code from `sendSessionResetCode`, at once (Q25). Like `changePassword`, every
     * other session ends and this device's new one is stored here.
     */
    resetPasswordInSession: async (code: string, newPassword: string): Promise<void> => {
      const pair = await this.request<TokenResponse>('/account/password-reset', {
        method: 'POST', body: { code, newPassword },
      });
      this.tokens.write(pair, this.tokens.read()?.membership ?? null);
    },

    /** Texts a reset code. The answer is the same whether or not the number has an account. */
    requestPasswordReset: (phone: string): Promise<PasswordResetRequestedResponse> =>
      this.request<PasswordResetRequestedResponse>('/auth/password-reset/request', {
        method: 'POST', body: { phone }, anonymous: true,
      }),

    /**
     * Sets the new password with the texted code, or, where it must wait (Q25), answers with the request it waits
     * on and changes nothing. Undefined means the password changed.
     */
    confirmPasswordReset: (phone: string, code: string, newPassword: string): Promise<RecoveryRequestResponse | undefined> =>
      this.request<RecoveryRequestResponse | undefined>('/auth/password-reset/confirm', {
        method: 'POST', body: { phone, code, newPassword }, anonymous: true,
      }),

    /** Sets a new password with a one-time recovery link from the platform team (Q25). */
    redeemRecoveryLink: (token: string, newPassword: string): Promise<void> =>
      this.request<void>('/auth/recovery-link/redeem', { method: 'POST', body: { token, newPassword }, anonymous: true }),

    /** Cancels a waiting reset of the caller's own password: "it wasn't me" (Q25). */
    cancelRecoveryRequest: (id: number): Promise<void> =>
      this.request<void>(`/account/recovery-requests/${id}/cancel`, { method: 'POST', body: {} }),
  };

  // ---- Tenant branding (anonymous, pre-login — D-1) -----------------------------------------------------

  branding(slug: string): Promise<TenantBrandingResponse> {
    return this.request<TenantBrandingResponse>(`/tenants/by-slug/${encodeURIComponent(slug)}/branding`, {
      anonymous: true,
    });
  }

  currentTenant(): Promise<TenantSettingsResponse> {
    return this.request<TenantSettingsResponse>('/tenants/current');
  }

  /** The nursery's names, contact, colour and weekend, by its own administration (`settings.manage`, C7). */
  updateCurrentTenant(body: UpdateTenantProfileRequest): Promise<TenantSettingsResponse> {
    return this.request<TenantSettingsResponse>('/tenants/current/profile', { method: 'PUT', body });
  }

  /** The configured limits the apps act on. The same for every member; see `useLimits`. */
  limits(): Promise<ClientLimits> {
    return this.request<ClientLimits>('/config/limits');
  }

  // ---- Platform scope ----------------------------------------------------------------------------------

  platform = {
    dashboard: (): Promise<PlatformDashboardResponse> =>
      this.request<PlatformDashboardResponse>('/platform/dashboard'),

    // The allergen catalogue (Q75–Q77). Every change reaches every child written with the names at once.
    listAllergens: (query: { search?: string; page?: number; pageSize?: number } = {}): Promise<PagedResponse<AllergenResponse>> =>
      this.request<PagedResponse<AllergenResponse>>('/platform/allergens', { query }),
    createAllergen: (body: CreateAllergenRequest): Promise<AllergenResponse> =>
      this.request<AllergenResponse>('/platform/allergens', { method: 'POST', body }),
    updateAllergen: (id: number, body: UpdateAllergenRequest): Promise<AllergenResponse> =>
      this.request<AllergenResponse>(`/platform/allergens/${id}`, { method: 'PUT', body }),
    addAllergenNames: (id: number, names: string[]): Promise<AllergenResponse> =>
      this.request<AllergenResponse>(`/platform/allergens/${id}/names`, { method: 'POST', body: { names } }),
    removeAllergenName: (id: number, aliasId: number): Promise<AllergenResponse> =>
      this.request<AllergenResponse>(`/platform/allergens/${id}/names/${aliasId}`, { method: 'DELETE' }),
    /** Moves every name of `fromId` into `id`; `fromId` leaves the picker. */
    mergeAllergens: (id: number, fromId: number): Promise<AllergenResponse> =>
      this.request<AllergenResponse>(`/platform/allergens/${id}/merge`, { method: 'POST', body: { fromId } }),
    retireAllergen: (id: number): Promise<AllergenResponse> =>
      this.request<AllergenResponse>(`/platform/allergens/${id}/retire`, { method: 'POST', body: {} }),
    restoreAllergen: (id: number): Promise<AllergenResponse> =>
      this.request<AllergenResponse>(`/platform/allergens/${id}/restore`, { method: 'POST', body: {} }),
    /** Names nurseries wrote that the catalogue does not know: the text and counts alone (Q77). */
    unmatchedAllergens: (query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<UnmatchedAllergenResponse>> =>
      this.request<PagedResponse<UnmatchedAllergenResponse>>('/platform/allergens/unmatched', { query }),

    // Backups (Q65): each action is queued, and followed through its operation.
    listBackups: (query: { page?: number; pageSize?: number } = {}): Promise<BackupListResponse> =>
      this.request<BackupListResponse>('/platform/backups', { query }),
    createBackup: (): Promise<BackupOperationResponse> =>
      this.request<BackupOperationResponse>('/platform/backups', { method: 'POST', body: {} }),
    verifyBackup: (id: string): Promise<BackupOperationResponse> =>
      this.request<BackupOperationResponse>(`/platform/backups/${encodeURIComponent(id)}/verify`, { method: 'POST', body: {} }),
    /** Restores the whole platform; needs a step-up token, the id typed out, and a reason. */
    restoreBackup: (id: string, body: { confirm: string; reason: string }, stepUp: string): Promise<BackupOperationResponse> =>
      this.request<BackupOperationResponse>(`/platform/backups/${encodeURIComponent(id)}/restore`, { method: 'POST', body, stepUp }),
    listBackupOperations: (query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<BackupOperationResponse>> =>
      this.request<PagedResponse<BackupOperationResponse>>('/platform/backups/operations', { query }),

    listTenants: (query: {
      search?: string; status?: string; page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<TenantProfileResponse>> =>
      this.request<PagedResponse<TenantProfileResponse>>('/platform/tenants', { query }),

    getTenant: (id: number): Promise<TenantProfileResponse> =>
      this.request<TenantProfileResponse>(`/platform/tenants/${id}`),

    /** Every module kind, whether it is built, and where the tenant stands with it (NR-11). */
    tenantModules: (id: number): Promise<TenantModuleResponse[]> =>
      this.request<TenantModuleResponse[]>(`/platform/tenants/${id}/modules`),

    /** Refused with 409 `module-not-built` for a module not in this release. */
    switchTenantModule: (id: number, module: TenantModuleKind, enabled: boolean): Promise<TenantModuleResponse> =>
      this.request<TenantModuleResponse>(`/platform/tenants/${id}/modules/${module}`, { method: 'PUT', body: { enabled } }),

    createTenant: (body: CreateTenantRequest): Promise<ProvisionedTenantResponse> =>
      this.request<ProvisionedTenantResponse>('/platform/tenants', { method: 'POST', body }),

    suspendTenant: (id: number, reason: string): Promise<TenantProfileResponse> =>
      this.request<TenantProfileResponse>(`/platform/tenants/${id}/suspend`, {
        method: 'POST', body: { reason },
      }),

    reactivateTenant: (id: number): Promise<TenantProfileResponse> =>
      this.request<TenantProfileResponse>(`/platform/tenants/${id}/reactivate`, { method: 'POST' }),

    /** The whole identity (C7). A new slug or timezone needs `confirmLinksAndTimes`. */
    updateTenant: (id: number, body: UpdateTenantRequest): Promise<TenantProfileResponse> =>
      this.request<TenantProfileResponse>(`/platform/tenants/${id}`, { method: 'PUT', body }),

    /** Ends a trial and makes the tenant active (`platform.billing.manage`, C6). */
    activateTenant: (id: number): Promise<TenantProfileResponse> =>
      this.request<TenantProfileResponse>(`/platform/tenants/${id}/activate`, { method: 'POST', body: {} }),

    /** Moves a trial's last day (`YYYY-MM-DD`, after today in the tenant's zone); the reminders follow it. */
    extendTrial: (id: number, endsOn: string): Promise<TenantProfileResponse> =>
      this.request<TenantProfileResponse>(`/platform/tenants/${id}/trial`, { method: 'POST', body: { endsOn } }),

    /** Sets or replaces a tenant's logo. Send what `toLogo` made: at most `media.logoMaximumEdge` on its longest side. */
    setTenantLogo: (id: number, logo: Blob): Promise<TenantLogoResponse> =>
      this.sendForm<TenantLogoResponse>(`/platform/tenants/${id}/logo`, fileForm(logo, 'logo.png'), 'PUT'),

    removeTenantLogo: (id: number): Promise<void> =>
      this.request<void>(`/platform/tenants/${id}/logo`, { method: 'DELETE' }),

    /** A new link for an owner who has not accepted yet. Every earlier link stops working. */
    resendOwnerInvitation: (tenantId: number): Promise<InvitationIssuedResponse> =>
      this.request<InvitationIssuedResponse>(`/platform/tenants/${tenantId}/owner-invitation`, { method: 'POST' }),

    // ---- Platform staff (FR-PLT-06) --------------------------------------------------------------------

    listUsers: (query: {
      status?: string; search?: string; page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<PlatformUserResponse>> =>
      this.request<PagedResponse<PlatformUserResponse>>('/platform/users', { query }),

    listRoles: (): Promise<PlatformRoleResponse[]> =>
      this.request<PlatformRoleResponse[]>('/platform/users/roles'),

    inviteUser: (body: InvitePlatformUserRequest): Promise<InvitedPlatformUserResponse> =>
      this.request<InvitedPlatformUserResponse>('/platform/users', { method: 'POST', body }),

    /** A new link for a platform user who has not accepted yet. Every earlier link stops working. */
    resendUserInvitation: (membershipId: number): Promise<InvitationIssuedResponse> =>
      this.request<InvitationIssuedResponse>(`/platform/users/${membershipId}/invitation`, { method: 'POST' }),

    assignRole: (membershipId: number, roleKey: string): Promise<void> =>
      this.request<void>(`/platform/users/${membershipId}/role`, {
        method: 'PATCH', body: { roleKey },
      }),

    disableUser: (membershipId: number): Promise<void> =>
      this.request<void>(`/platform/users/${membershipId}/disable`, { method: 'POST' }),

    enableUser: (membershipId: number): Promise<void> =>
      this.request<void>(`/platform/users/${membershipId}/enable`, { method: 'POST' }),

    // ---- Platform announcements (FR-PLT-07) ------------------------------------------------------------

    listAnnouncements: (query: {
      page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<PlatformAnnouncementResponse>> =>
      this.request<PagedResponse<PlatformAnnouncementResponse>>('/platform/announcements', { query }),

    createAnnouncement: (
      body: CreatePlatformAnnouncementRequest,
    ): Promise<PlatformAnnouncementResponse> =>
      this.request<PlatformAnnouncementResponse>('/platform/announcements', { method: 'POST', body }),

    previewAnnouncementAudience: (id: number): Promise<AnnouncementDeliveryResponse> =>
      this.request<AnnouncementDeliveryResponse>(`/platform/announcements/${id}/audience`),

    sendAnnouncement: (id: number): Promise<AnnouncementDeliveryResponse> =>
      this.request<AnnouncementDeliveryResponse>(`/platform/announcements/${id}/send`, {
        method: 'POST',
      }),

    deleteAnnouncementDraft: (id: number): Promise<void> =>
      this.request<void>(`/platform/announcements/${id}`, { method: 'DELETE' }),

    // ---- Platform audit (FR-PLT-08) --------------------------------------------------------------------

    auditLog: (query: {
      tenantId?: number; actorUserId?: number; entityTypeId?: number; action?: string;
      supportSessionsOnly?: boolean; from?: string; to?: string; page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<PlatformAuditEntryResponse>> =>
      this.request<PagedResponse<PlatformAuditEntryResponse>>('/platform/audit', { query }),

    auditEntityTypes: (): Promise<AuditEntityOptionResponse[]> =>
      this.request<AuditEntityOptionResponse[]>('/platform/audit/entities'),
  };

  // ---- Tenant scope ------------------------------------------------------------------------------------

  /** The platform's allergen catalogue as the allergy editor offers it (Q75). See `useAllergenCatalogue`. */
  allergens = {
    list: (query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<AllergenOptionResponse>> =>
      this.request<PagedResponse<AllergenOptionResponse>>('/allergens', { query }),
  };

  children = {
    list: (query: {
      classId?: number; status?: ChildStatus; search?: string; page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<ChildSummaryResponse>> =>
      this.request<PagedResponse<ChildSummaryResponse>>('/children', { query }),

    get: (id: number): Promise<ChildDetailResponse> =>
      this.request<ChildDetailResponse>(`/children/${id}`),

    /** Sets or replaces a child's photo. Needs `children.update`. */
    setPhoto: (id: number, photo: Blob): Promise<ChildPhotoResponse> =>
      this.sendForm<ChildPhotoResponse>(`/children/${id}/photo`, fileForm(photo, 'photo.jpg'), 'PUT'),

    removePhoto: (id: number): Promise<void> =>
      this.request<void>(`/children/${id}/photo`, { method: 'DELETE' }),

    /** Replaces the emergency contacts; at least one is required (Q9). Needs `children.update`. */
    replaceEmergencyContacts: (id: number, emergencyContacts: CreateChildRequest['emergencyContacts']): Promise<void> =>
      this.request<void>(`/children/${id}/emergency-contacts`, { method: 'PUT', body: { emergencyContacts } }),

    /** Replaces the allergy answer: the allergies, or none known (Q9). Needs `children.update`. */
    replaceAllergies: (
      id: number, allergies: CreateChildRequest['allergies'], noKnownAllergies: boolean,
    ): Promise<void> =>
      this.request<void>(`/children/${id}/allergies`, { method: 'PUT', body: { allergies, noKnownAllergies } }),

    /** Active children still missing an emergency contact or an allergy answer (Q9). Needs `children.update`. */
    registrationGaps: (query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<RegistrationGapResponse>> =>
      this.request<PagedResponse<RegistrationGapResponse>>('/children/registration-gaps', { query }),

    /** Active children with a consent nobody has answered, so the administration can complete them (Q45). */
    consentGaps: (query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<ConsentGapResponse>> =>
      this.request<PagedResponse<ConsentGapResponse>>('/children/consent-gaps', { query }),

    create: (body: CreateChildRequest): Promise<ChildDetailResponse> =>
      this.request<ChildDetailResponse>('/children', { method: 'POST', body }),

    update: (id: number, body: {
      firstName?: string | null; lastName?: string | null; preferredName?: string | null;
      dateOfBirth?: string | null; gender?: string | null;
      nationalId?: string | null; notes?: string | null;
      /** Moves the open enrolment period too; refused past a recorded day or across a closed term. */
      enrollmentDate?: string | null;
      diaperLog?: DiaperLogChoice | null;
    }): Promise<void> =>
      this.request<void>(`/children/${id}`, { method: 'PATCH', body }),

    withdraw: (
      id: number, reason: string | null, withdrawalDate: string | null,
    ): Promise<void> =>
      this.request<void>(`/children/${id}/withdraw`, {
        method: 'POST', body: { reason, withdrawalDate },
      }),

    /** History stays attached to the class it was recorded under; a transfer never rewrites the past. */
    transfer: (id: number, body: {
      toClassId: number; effectiveDate: string | null; reason: string | null;
    }): Promise<{
      childId: number; toClassId: number; effectiveDate: string;
      overCapacity: boolean; capacity: number; occupancy: number;
    }> =>
      this.request(`/children/${id}/transfer`, { method: 'POST', body }),

    /** Opens a new stay; the links the withdrawal ended come back only where named (Q32). */
    reenroll: (id: number, body: {
      classId: number | null; enrollmentDate: string | null; restoreGuardianLinkIds?: number[];
    }): Promise<void> =>
      this.request<void>(`/children/${id}/reenroll`, { method: 'POST', body }),

    /**
     * Validates or commits a bulk import.
     *
     * Call with `commit: false` first and show the per-row verdicts: a commit is all-or-nothing, because a
     * half-imported register is worse than none — nobody can tell which half landed (FR-ADM-21).
     */
    import: (rows: ImportRow[], commit: boolean): Promise<ImportResultResponse> =>
      this.request<ImportResultResponse>('/children/import', { method: 'POST', body: { rows, commit } }),
  };

  classes = {
    list: (query: { status?: string; academicYearId?: number; page?: number; pageSize?: number } = {}):
      Promise<PagedResponse<ClassResponse>> =>
      this.request<PagedResponse<ClassResponse>>('/classes', { query }),

    create: (body: CreateClassRequest): Promise<ClassResponse> =>
      this.request<ClassResponse>('/classes', { method: 'POST', body }),

    update: (id: number, body: CreateClassRequest): Promise<ClassResponse> =>
      this.request<ClassResponse>(`/classes/${id}`, { method: 'PATCH', body }),

    /** Refused while children are still in it (FR-ADM-13). */
    archive: (id: number): Promise<void> =>
      this.request<void>(`/classes/${id}/archive`, { method: 'POST' }),
  };

  // ---- Academic calendar (Q33–Q35) ----------------------------------------------------------------------

  academicYears = {
    /** Latest first. Every member reads it; `academic.manage` changes it. */
    list: (query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<AcademicYearResponse>> =>
      this.request<PagedResponse<AcademicYearResponse>>('/academic-years', { query }),

    get: (id: number): Promise<AcademicYearResponse> => this.request<AcademicYearResponse>(`/academic-years/${id}`),

    /** Planned, and divided into the tenant's `academic.terms_per_year` terms. */
    create: (body: CalendarSpanRequest): Promise<AcademicYearResponse> =>
      this.request<AcademicYearResponse>('/academic-years', { method: 'POST', body }),

    update: (id: number, body: CalendarSpanRequest): Promise<AcademicYearResponse> =>
      this.request<AcademicYearResponse>(`/academic-years/${id}`, { method: 'PUT', body }),

    /** The year that was current goes back to planned; nothing is closed. */
    makeCurrent: (id: number): Promise<void> =>
      this.request<void>(`/academic-years/${id}/make-current`, { method: 'POST' }),

    /** A planned year no class belongs to. */
    remove: (id: number): Promise<void> => this.request<void>(`/academic-years/${id}`, { method: 'DELETE' }),

    addTerm: (yearId: number, body: CalendarSpanRequest): Promise<TermResponse> =>
      this.request<TermResponse>(`/academic-years/${yearId}/terms`, { method: 'POST', body }),

    updateTerm: (id: number, body: CalendarSpanRequest): Promise<TermResponse> =>
      this.request<TermResponse>(`/terms/${id}`, { method: 'PUT', body }),

    removeTerm: (id: number): Promise<void> => this.request<void>(`/terms/${id}`, { method: 'DELETE' }),

    /** Its records become read only (Q34). Refused while the term is still running. */
    closeTerm: (id: number): Promise<TermResponse> =>
      this.request<TermResponse>(`/terms/${id}/close`, { method: 'POST' }),

    /** What closing the current year would do: proposed copies, and each child's class or graduation (Q36). */
    rolloverPreview: (id: number): Promise<RolloverPreviewResponse> =>
      this.request<RolloverPreviewResponse>(`/academic-years/${id}/rollover-preview`),

    /** Closes the current year as the reviewed plan says, and makes the next one current. Refused before it ends. */
    close: (id: number, body: CloseAcademicYearRequest): Promise<CloseAcademicYearResponse> =>
      this.request<CloseAcademicYearResponse>(`/academic-years/${id}/close`, { method: 'POST', body }),

    /** The owner's alone (`academic.reopen`), with a reason the audit trail keeps. */
    reopenTerm: (id: number, reason: string): Promise<TermResponse> =>
      this.request<TermResponse>(`/terms/${id}/reopen`, { method: 'POST', body: { reason } }),
  };

  // ---- Catalogues ---------------------------------------------------------------------------------------

  catalog = {
    list: (query: { type?: CatalogType; includeInactive?: boolean } = {}): Promise<CatalogItemResponse[]> =>
      this.request<CatalogItemResponse[]>('/catalog', { query }),

    create: (body: {
      catalogType: CatalogType; nameAr: string; nameEn: string | null;
      icon: string | null; sortOrder: number; unitAr?: string | null; unitEn?: string | null;
    }): Promise<CatalogItemResponse> =>
      this.request<CatalogItemResponse>('/catalog', { method: 'POST', body }),

    update: (id: number, body: {
      nameAr?: string | null; nameEn?: string | null; icon?: string | null;
      sortOrder?: number | null; isActive?: boolean | null;
      /** An empty string clears the unit. */
      unitAr?: string | null; unitEn?: string | null;
    }): Promise<CatalogItemResponse> =>
      this.request<CatalogItemResponse>(`/catalog/${id}`, { method: 'PATCH', body }),

    retire: (id: number): Promise<void> =>
      this.request<void>(`/catalog/${id}`, { method: 'DELETE' }),
  };

  // ---- Daily care ---------------------------------------------------------------------------------------

  daily = {
    /** One child's day. `date` omitted means the tenant's today. */
    forChild: (childId: number, date?: string): Promise<DailyReportResponse> =>
      this.request<DailyReportResponse>(`/children/${childId}/day`, { query: { date } }),

    /** A class's board, with what is still missing per child. */
    forClass: (classId: number, date?: string): Promise<ClassDaySummaryResponse[]> =>
      this.request<ClassDaySummaryResponse[]>(`/classes/${classId}/day`, { query: { date } }),

    /**
     * Records one entry for one child or for several at once.
     *
     * Pass `clientEntryId` for anything that may be retried — the offline queue always does, so a response
     * lost on the way back never becomes a second meal.
     */
    record: (body: RecordCareEntryRequest): Promise<RecordCareEntryResponse> =>
      this.request<RecordCareEntryResponse>('/care-entries', { method: 'POST', body }),

    update: (kind: CareEntryKind, id: number, body: Record<string, unknown>): Promise<void> =>
      this.request<void>(`/care-entries/${kind}/${id}`, { method: 'PATCH', body }),

    remove: (kind: CareEntryKind, id: number): Promise<void> =>
      this.request<void>(`/care-entries/${kind}/${id}`, { method: 'DELETE' }),

    publish: (childId: number, date?: string): Promise<void> =>
      this.request<void>(`/children/${childId}/day/publish`, { method: 'POST', query: { date } }),
  };

  // ---- Attendance ---------------------------------------------------------------------------------------

  attendance = {
    board: (query: { date?: string; classId?: number; search?: string } = {}):
      Promise<AttendanceBoardResponse> =>
      this.request<AttendanceBoardResponse>('/attendance', { query }),

    checkIn: (body: {
      childId: number; date?: string | null; at?: string | null;
      temperatureDeciCelsius?: number | null; symptomsNote?: string | null;
    }): Promise<{ id: number; status: AttendanceStatus; checkInAt: string }> =>
      this.request('/attendance/check-in', { method: 'POST', body }),

    /** One collector at most: a guardian by link, an authorised pickup, or an override with a reason (Q30). */
    checkOut: (body: {
      childId: number; date?: string | null; at?: string | null;
      guardianLinkId?: number | null;
      authorizedPickupId?: number | null;
      overridePersonName?: string | null; overrideReason?: string | null;
      /** With an override, that the do-not-release list was read; required where the child has one. */
      acknowledgedRestrictions?: boolean;
    }): Promise<{ id: number; status: AttendanceStatus; checkOutAt: string }> =>
      this.request('/attendance/check-out', { method: 'POST', body }),

    correct: (id: number, body: {
      checkInAt: string | null; checkOutAt: string | null; reason: string | null;
    }): Promise<void> =>
      this.request<void>(`/attendance/${id}`, { method: 'PATCH', body }),

    history: (childId: number, query: { from?: string; to?: string } = {}):
      Promise<AttendanceSummaryResponse> =>
      this.request<AttendanceSummaryResponse>(`/children/${childId}/attendance`, { query }),

    notices: (query: {
      from?: string; to?: string; upcoming?: boolean; childId?: number; page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<AbsenceNoticeResponse>> =>
      this.request<PagedResponse<AbsenceNoticeResponse>>('/absence-notices', { query }),

    notifyAbsence: (childId: number, body: {
      dateFrom: string; dateTo: string | null; reason: string | null;
    }): Promise<AbsenceNoticeResponse> =>
      this.request<AbsenceNoticeResponse>(`/children/${childId}/absence-notices`, { method: 'POST', body }),

    cancelNotice: (id: number): Promise<void> =>
      this.request<void>(`/absence-notices/${id}`, { method: 'DELETE' }),
  };

  // ---- Photographs and consent --------------------------------------------------------------------------

  photos = {
    /**
     * The gallery. `classId` narrows to the class's children today, `mine` to the caller's uploads, and a
     * `status` other than `Published` lists what staff with `photos.publish` may republish.
     */
    gallery: (query: {
      childId?: number; occasionId?: number; from?: string; to?: string;
      classId?: number; mine?: boolean; status?: PhotoStatus;
      page?: number; pageSize?: number;
    } = {}): Promise<PhotoGalleryResponse> =>
      this.request<PhotoGalleryResponse>('/photos', { query }),

    /**
     * Uploads one image with its metadata.
     *
     * Multipart rather than JSON, so the bytes are streamed rather than base64-encoded into a payload a
     * third larger — which matters on the 3G connection this product is built for.
     */
    upload: async (file: File, metadata: PhotoUploadMetadata): Promise<PhotoResponse> => {
      const form = new FormData();
      form.append('file', file);
      form.append('metadata', JSON.stringify(metadata));
      return this.sendForm<PhotoResponse>('/photos', form);
    },

    update: (id: number, body: {
      childIds?: number[] | null; caption?: string | null;
      occasionId?: number | null; visibilityMode?: PhotoVisibilityMode | null; clearOccasion?: boolean;
    }): Promise<void> =>
      this.request<void>(`/photos/${id}`, { method: 'PATCH', body }),

    publish: (id: number): Promise<void> =>
      this.request<void>(`/photos/${id}/publish`, { method: 'POST' }),

    remove: (id: number): Promise<void> =>
      this.request<void>(`/photos/${id}`, { method: 'DELETE' }),

    occasions: (): Promise<OccasionResponse[]> => this.request<OccasionResponse[]>('/occasions'),

    createOccasion: (body: {
      name: string; typeCatalogItemId: number | null; date: string;
    }): Promise<OccasionResponse> =>
      this.request<OccasionResponse>('/occasions', { method: 'POST', body }),
  };

  consents = {
    list: (childId: number): Promise<ConsentResponse[]> =>
      this.request<ConsentResponse[]>(`/children/${childId}/consents`),

    /**
     * Staff only, from the signed form (Q23); a grant needs `evidenceRef`. Withdrawing a photo consent pulls the
     * pictures it no longer allows from every gallery and chat at once.
     */
    record: (childId: number, body: {
      consentType: ConsentType; granted: boolean; evidenceRef: string | null;
      guardianMembershipId?: number | null;
    }): Promise<{ childId: number; consentType: ConsentType; granted: boolean; withdrawnPhotos: number }> =>
      this.request(`/children/${childId}/consents`, { method: 'POST', body }),
  };

  // ---- Messaging ----------------------------------------------------------------------------------------

  conversations = {
    /**
     * `unread` keeps the threads with (true) or without (false) an unread message, by the badge's rule; `from` and
     * `to` (YYYY-MM-DD, the nursery's days) keep the threads whose last message falls on them (NR-16).
     */
    list: (query: ConversationListQuery = {}):
      Promise<PagedResponse<ConversationResponse>> =>
      this.request<PagedResponse<ConversationResponse>>('/conversations', { query }),

    /** One of the caller's threads, the row the list shows, wherever it falls in the list. */
    get: (id: number): Promise<ConversationResponse> =>
      this.request<ConversationResponse>(`/conversations/${id}`),

    /** How many of the caller's conversations hold an unread message, across every page. */
    unreadCount: (): Promise<UnreadCountResponse> =>
      this.request<UnreadCountResponse>('/conversations/unread-count'),

    /** Exactly the people a message to whom would actually be accepted. */
    contacts: (): Promise<ContactResponse[]> => this.request<ContactResponse[]>('/conversations/contacts'),

    start: (body: {
      participantMembershipIds: number[]; childContextId: number | null;
      subject: string | null; body: string;
    }): Promise<MessageResponse> =>
      this.request<MessageResponse>('/conversations', { method: 'POST', body }),

    messages: (id: number, query: { page?: number; pageSize?: number } = {}):
      Promise<PagedResponse<MessageResponse>> =>
      this.request<PagedResponse<MessageResponse>>(`/conversations/${id}/messages`, { query }),

    /**
     * Every word of `q` in a message of this thread, folded as the server folds (NR-16), newest first. A hit's
     * `position` opens page `Math.floor(position / pageSize) + 1` of `messages`.
     */
    searchMessages: (id: number, q: string, query: { page?: number; pageSize?: number } = {}):
      Promise<PagedResponse<MessageSearchHit>> =>
      this.request<PagedResponse<MessageSearchHit>>(`/conversations/${id}/messages/search`, { query: { ...query, q } }),

    /** Text, gallery items by id, or both. Only staff may share gallery items. */
    send: (id: number, body: string, photoIds: number[] = []): Promise<MessageResponse> =>
      this.request<MessageResponse>(`/conversations/${id}/messages`, {
        method: 'POST',
        body: photoIds.length > 0 ? { body, photoIds } : { body },
      }),

    /** Sends a recording as it was made, within the configured size and `chat.voiceNoteMaximumMs`, typed by the server from its bytes. */
    sendVoiceNote: (id: number, recording: Blob, durationMs: number): Promise<MessageResponse> => {
      const form = new FormData();
      form.append('file', recording, 'voice-note');
      form.append('durationMs', String(Math.round(durationMs)));
      return this.sendForm<MessageResponse>(`/conversations/${id}/voice-notes`, form);
    },

    /**
     * Guardians only (Q46): a picture from the phone, as `toChatImage` made it, with optional text. Staff are
     * refused and share from the gallery instead.
     */
    sendImage: (id: number, image: Blob, body = ''): Promise<MessageResponse> => {
      const form = new FormData();
      form.append('file', image, 'image.jpg');
      if (body) form.append('body', body);
      return this.sendForm<MessageResponse>(`/conversations/${id}/images`, form);
    },

    /** Staff only: the child's gallery items every family in the thread may already see. */
    shareableMedia: (id: number, query: { page?: number; pageSize?: number } = {}):
      Promise<PagedResponse<ShareableMediaResponse>> =>
      this.request<PagedResponse<ShareableMediaResponse>>(`/conversations/${id}/shareable-media`, { query }),

    /** The reader's own notifications about the conversation's child, oldest first, at most 100. */
    events: (id: number, query: { from?: string; to?: string } = {}): Promise<ConversationEventResponse[]> =>
      this.request<ConversationEventResponse[]>(`/conversations/${id}/events`, { query }),

    /** Only the sender may delete a message. A voice note or picture it carried is deleted with it. */
    deleteMessage: (id: number, messageId: number): Promise<void> =>
      this.request<void>(`/conversations/${id}/messages/${messageId}`, { method: 'DELETE' }),

    markRead: (id: number): Promise<void> =>
      this.request<void>(`/conversations/${id}/read`, { method: 'POST' }),
  };

  announcements = {
    list: (query: { page?: number; pageSize?: number } = {}):
      Promise<PagedResponse<AnnouncementResponse>> =>
      this.request<PagedResponse<AnnouncementResponse>>('/announcements', { query }),

    create: (body: CreateAnnouncementRequest): Promise<AnnouncementResponse> =>
      this.request<AnnouncementResponse>('/announcements', { method: 'POST', body }),

    send: (id: number): Promise<void> =>
      this.request<void>(`/announcements/${id}/send`, { method: 'POST' }),

    markRead: (id: number): Promise<void> =>
      this.request<void>(`/announcements/${id}/read`, { method: 'POST' }),
  };

  /**
   * Complaints (§4.14).
   *
   * One section for both sides. Which of these a given account may call is decided by the API, not by which
   * app is calling — a guardian gets 403 on `assign` and a handler gets 404 on a complaint that names them,
   * and neither answer depends on the client asking nicely.
   */
  complaints = {
    list: (query: {
      status?: ComplaintStatus; category?: ComplaintCategory; assignedToMe?: boolean;
      openOnly?: boolean; childId?: number; page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<ComplaintResponse>> =>
      this.request<PagedResponse<ComplaintResponse>>('/complaints', { query }),

    get: (id: number): Promise<ComplaintResponse> =>
      this.request<ComplaintResponse>(`/complaints/${id}`),

    create: (body: CreateComplaintRequest): Promise<ComplaintResponse> =>
      this.request<ComplaintResponse>('/complaints', { method: 'POST', body }),

    timeline: (id: number, query: { page?: number; pageSize?: number } = {}):
      Promise<PagedResponse<ComplaintEventResponse>> =>
      this.request<PagedResponse<ComplaintEventResponse>>(`/complaints/${id}/timeline`, { query }),

    /** Who a complaint may be handed to. Pass the complaint so the person it names is left out. */
    assignees: (complaintId?: number): Promise<ComplaintAssigneeResponse[]> =>
      this.request<ComplaintAssigneeResponse[]>('/complaints/assignees', {
        query: complaintId === undefined ? {} : { complaintId },
      }),

    acknowledge: (id: number): Promise<void> =>
      this.request<void>(`/complaints/${id}/acknowledge`, { method: 'POST' }),

    assign: (id: number, assignedToMembershipId: number, note?: string): Promise<void> =>
      this.request<void>(`/complaints/${id}/assign`, {
        method: 'POST',
        body: { assignedToMembershipId, note: note ?? null },
      }),

    /** Internal. Never reaches the family (FR-CMP-08). */
    addNote: (id: number, body: string): Promise<void> =>
      this.request<void>(`/complaints/${id}/notes`, { method: 'POST', body: { body } }),

    /** Written to the other side, and read by them. */
    reply: (id: number, body: string): Promise<void> =>
      this.request<void>(`/complaints/${id}/replies`, { method: 'POST', body: { body } }),

    update: (id: number, body: { severity?: ComplaintSeverity; markInProgress?: boolean }): Promise<void> =>
      this.request<void>(`/complaints/${id}`, { method: 'PATCH', body }),

    resolve: (id: number, resolutionSummary: string): Promise<void> =>
      this.request<void>(`/complaints/${id}/resolve`, { method: 'POST', body: { resolutionSummary } }),

    close: (id: number): Promise<void> =>
      this.request<void>(`/complaints/${id}/close`, { method: 'POST' }),

    reopen: (id: number): Promise<void> =>
      this.request<void>(`/complaints/${id}/reopen`, { method: 'POST' }),

    withdraw: (id: number): Promise<void> =>
      this.request<void>(`/complaints/${id}/withdraw`, { method: 'POST' }),
  };

  calendar = {
    list: (query: { from?: string; to?: string } = {}): Promise<CalendarEventResponse[]> =>
      this.request<CalendarEventResponse[]>('/calendar', { query }),

    create: (body: CreateCalendarEventRequest): Promise<CalendarEventResponse> =>
      this.request<CalendarEventResponse>('/calendar', { method: 'POST', body }),

    /** Replaces the entry as `create` writes it. A closure in, or moving into, a closed term is refused (Q34). */
    update: (id: number, body: CreateCalendarEventRequest): Promise<CalendarEventResponse> =>
      this.request<CalendarEventResponse>(`/calendar/${id}`, { method: 'PUT', body }),

    remove: (id: number): Promise<void> =>
      this.request<void>(`/calendar/${id}`, { method: 'DELETE' }),
  };

  // ---- Finance ------------------------------------------------------------------------------------------

  finance = {
    feePlans: (query: { includeInactive?: boolean } = {}): Promise<FeePlanResponse[]> =>
      this.request<FeePlanResponse[]>('/fee-plans', { query }),

    createFeePlan: (body: CreateFeePlanRequest): Promise<FeePlanResponse> =>
      this.request<FeePlanResponse>('/fee-plans', { method: 'POST', body }),

    updateFeePlan: (id: number, body: {
      name?: string | null; amountMinorUnits?: number | null;
      prorationPolicy?: ProrationPolicy | null; isActive?: boolean | null;
    }): Promise<void> =>
      this.request<void>(`/fee-plans/${id}`, { method: 'PATCH', body }),

    setEnrollment: (childId: number, body: {
      feePlanId: number; startDate: string | null;
      discountType?: DiscountType; discountValue?: number; discountReason?: string | null;
    }): Promise<EnrollmentResponse> =>
      this.request<EnrollmentResponse>(`/children/${childId}/enrollment`, { method: 'POST', body }),

    addCharge: (childId: number, body: {
      description: string; amountMinorUnits: number; quantity?: number;
    }): Promise<{ invoiceId: number; lineId: number; totalMinorUnits: number }> =>
      this.request(`/children/${childId}/charges`, { method: 'POST', body }),

    /** Sets or changes a child's total for a year (Q44). The same total and deadline again changes nothing. */
    setYearFee: (childId: number, body: SetYearFeeRequest): Promise<YearFeeSummary> =>
      this.request<YearFeeSummary>(`/children/${childId}/year-fee`, { method: 'PUT', body }),

    /** Each child's total for a year, those who still owe first; the current year unless one is named. */
    yearFees: (query: {
      academicYearId?: number; status?: YearFeeStatus; page?: number; pageSize?: number;
    } = {}): Promise<YearFeeListResponse> =>
      this.request<YearFeeListResponse>('/finance/year-fees', { query }),

    /** A child's account, in the shape the client's own ledger had it. */
    account: (childId: number): Promise<ChildAccountResponse> =>
      this.request<ChildAccountResponse>(`/children/${childId}/account`),

    /** The statement of account as a PDF, for the nursery's file: staff only (Q41), `finance.statement.generate`. */
    statementPdf: (childId: number): Promise<Blob> =>
      this.file(`/children/${childId}/statement.pdf`, 'application/pdf'),

    invoices: (query: {
      childId?: number; status?: InvoiceStatus; from?: string; to?: string;
      page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<InvoiceResponse>> =>
      this.request<PagedResponse<InvoiceResponse>>('/invoices', { query }),

    invoice: (id: number): Promise<InvoiceResponse> => this.request<InvoiceResponse>(`/invoices/${id}`),

    generateInvoices: (body: {
      periodStart: string; childIds: number[] | null; issueImmediately: boolean;
    }): Promise<GenerateInvoicesResponse> =>
      this.request<GenerateInvoicesResponse>('/invoices/generate', { method: 'POST', body }),

    issueInvoice: (id: number): Promise<void> =>
      this.request<void>(`/invoices/${id}/issue`, { method: 'POST' }),

    voidInvoice: (id: number, reason: string): Promise<void> =>
      this.request<void>(`/invoices/${id}/void`, { method: 'POST', body: { reason } }),

    payments: (query: {
      childId?: number; from?: string; to?: string; page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<PaymentResponse>> =>
      this.request<PagedResponse<PaymentResponse>>('/payments', { query }),

    recordPayment: (body: RecordPaymentRequest): Promise<PaymentResponse> =>
      this.request<PaymentResponse>('/payments', { method: 'POST', body }),

    voidPayment: (id: number, reason: string): Promise<void> =>
      this.request<void>(`/payments/${id}/void`, { method: 'POST', body: { reason } }),

    overdue: (query: { sort?: 'amount' | 'days' } = {}): Promise<OverdueListResponse> =>
      this.request<OverdueListResponse>('/finance/overdue', { query }),

    income: (query: { from?: string; to?: string } = {}): Promise<IncomeReportResponse> =>
      this.request<IncomeReportResponse>('/finance/income', { query }),

    sendReminders: (childIds: number[] | null): Promise<{ sent: number; skipped: number }> =>
      this.request('/finance/reminders', { method: 'POST', body: { childIds } }),
  };

  // ---- Reports (FR-ADM-20) ------------------------------------------------------------------------------

  /**
   * Each report is behind `reports.view` and the permission of what it counts: attendance, the day, the register
   * (`children.update`) or money. Printed ones are staff documents, watermarked for whoever printed them (C8).
   */
  reports = {
    /** A class's attendance over one month, child by child; the current month unless one is named. */
    attendance: (classId: number, query: ReportMonthQuery & { page?: number; pageSize?: number } = {}): Promise<AttendanceReportResponse> =>
      this.request<AttendanceReportResponse>(`/reports/classes/${classId}/attendance`, { query }),

    attendancePdf: (classId: number, query: ReportMonthQuery = {}): Promise<Blob> =>
      this.file(`/reports/classes/${classId}/attendance.pdf`, 'application/pdf', query),

    /** How complete each person's classes' days were over one month. */
    dailyCare: (query: ReportMonthQuery & { page?: number; pageSize?: number } = {}): Promise<DailyCareReportResponse> =>
      this.request<DailyCareReportResponse>('/reports/daily-care', { query }),

    dailyCarePdf: (query: ReportMonthQuery = {}): Promise<Blob> =>
      this.file('/reports/daily-care.pdf', 'application/pdf', query),

    /** The same month by who recorded each entry: each person's entries by kind, and the days they recorded on. */
    dailyCareRecorders: (query: ReportMonthQuery & { page?: number; pageSize?: number } = {}): Promise<DailyCareRecordersResponse> =>
      this.request<DailyCareRecordersResponse>('/reports/daily-care/recorders', { query }),

    dailyCareRecordersPdf: (query: ReportMonthQuery = {}): Promise<Blob> =>
      this.file('/reports/daily-care/recorders.pdf', 'application/pdf', query),

    /** The enrolled children with their class and guardians; one class's where named. */
    roster: (query: { classId?: number; page?: number; pageSize?: number } = {}): Promise<PagedResponse<RosterRow>> =>
      this.request<PagedResponse<RosterRow>>('/reports/roster', { query }),

    rosterPdf: (query: { classId?: number } = {}): Promise<Blob> =>
      this.file('/reports/roster.pdf', 'application/pdf', query),

    /** Who still owes on a year's fees: the current year unless one is named. */
    balancesPdf: (query: { academicYearId?: number } = {}): Promise<Blob> =>
      this.file('/reports/balances.pdf', 'application/pdf', query),

    /** Income over a period, as `finance.income` shows it. */
    incomePdf: (query: { from?: string; to?: string } = {}): Promise<Blob> =>
      this.file('/reports/income.pdf', 'application/pdf', query),
  };

  // ---- Health and safety --------------------------------------------------------------------------------

  health = {
    incidents: (query: {
      childId?: number; unacknowledged?: boolean; page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<IncidentResponse>> =>
      this.request<PagedResponse<IncidentResponse>>('/incidents', { query }),

    incident: (id: number): Promise<IncidentResponse> => this.request<IncidentResponse>(`/incidents/${id}`),

    /** The incident record as a PDF, for the nursery's file: staff only (Q16, Q41), `health.view`. */
    incidentPdf: (id: number): Promise<Blob> => this.file(`/incidents/${id}/pdf`, 'application/pdf'),

    createIncident: (body: CreateIncidentRequest): Promise<IncidentResponse> =>
      this.request<IncidentResponse>('/incidents', { method: 'POST', body }),

    acknowledgeIncident: (id: number): Promise<void> =>
      this.request<void>(`/incidents/${id}/acknowledge`, { method: 'POST' }),

    closeIncident: (id: number, body: CloseIncidentRequest): Promise<void> =>
      this.request<void>(`/incidents/${id}/close`, { method: 'POST', body }),

    record: (childId: number): Promise<ChildHealthResponse> =>
      this.request<ChildHealthResponse>(`/children/${childId}/health`),

    updateRecord: (childId: number, body: Partial<Omit<ChildHealthResponse, 'childId'>>): Promise<void> =>
      this.request<void>(`/children/${childId}/health`, { method: 'PUT', body }),

    medications: (childId: number): Promise<MedicationResponse[]> =>
      this.request<MedicationResponse[]>(`/children/${childId}/medications`),

    /** The round: what a class's children have in force today, with today's doses. `medication.administer`. */
    dueForClass: (classId: number, query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<MedicationResponse>> =>
      this.request<PagedResponse<MedicationResponse>>(`/classes/${classId}/medications/due`, { query }),

    /** Staff with `health.manage` only; guardians enter nothing (Q17). */
    authorizeMedication: (childId: number, body: {
      medicationName: string; dose: string; formId: number; schedule: string;
      maxDosesPerDay: number | null; startDate: string | null; endDate: string | null;
      notes: string | null; informedByGuardianMembershipId?: number | null;
    }): Promise<{ id: number }> =>
      this.request(`/children/${childId}/medications`, { method: 'POST', body }),

    administer: (medicationId: number, body: {
      doseGiven: string; administeredAt: string | null;
      note: string | null; outOfScheduleReason: string | null;
    }): Promise<{ id: number; administeredAt: string }> =>
      this.request(`/medications/${medicationId}/administer`, { method: 'POST', body }),
  };

  // ---- Staff, guardians and pickups ---------------------------------------------------------------------

  /**
   * What the nursery does for someone who cannot get into their account (Q25, Q27). Every call needs
   * `accounts.recover`, and only for a person who belongs to this nursery alone and does not run it.
   */
  accountRecovery = {
    /** Password resets waiting for this nursery to confirm the person. */
    list: (page = 1): Promise<PagedResponse<CentreRecoveryRequestResponse>> =>
      this.request<PagedResponse<CentreRecoveryRequestResponse>>('/admin/recovery-requests', { query: { page } }),

    /** "Confirmed in person": the person may now set a password with a fresh code. */
    approve: (id: number): Promise<RecoveryRequestResponse> =>
      this.request<RecoveryRequestResponse>(`/admin/recovery-requests/${id}/approve`, { method: 'POST', body: {} }),

    reject: (id: number): Promise<RecoveryRequestResponse> =>
      this.request<RecoveryRequestResponse>(`/admin/recovery-requests/${id}/reject`, { method: 'POST', body: {} }),

    /** Sends a link to the new number; the account moves when it is opened. */
    renumber: (membershipId: number, newPhone: string): Promise<PhoneChangeRequestedResponse> =>
      this.request<PhoneChangeRequestedResponse>(`/admin/memberships/${membershipId}/renumber`, {
        method: 'POST', body: { newPhone },
      }),

    /** Takes the number off the account, for whoever holds it now. */
    release: (membershipId: number): Promise<NumberReleasedResponse> =>
      this.request<NumberReleasedResponse>(`/admin/memberships/${membershipId}/release`, { method: 'POST', body: {} }),
  };

  /**
   * What the platform team does for someone no single nursery may decide for (Q25, Q27). Every call needs
   * `platform.identities.recover`; a platform member's own account also needs `platform.users.manage`.
   */
  platformRecovery = {
    list: (page = 1): Promise<PagedResponse<PlatformRecoveryRequestResponse>> =>
      this.request<PagedResponse<PlatformRecoveryRequestResponse>>('/platform/recovery-requests', { query: { page } }),

    approve: (id: number): Promise<RecoveryRequestResponse> =>
      this.request<RecoveryRequestResponse>(`/platform/recovery-requests/${id}/approve`, { method: 'POST', body: {} }),

    reject: (id: number): Promise<RecoveryRequestResponse> =>
      this.request<RecoveryRequestResponse>(`/platform/recovery-requests/${id}/reject`, { method: 'POST', body: {} }),

    /** The account a number belongs to: an empty page where none does. */
    find: (phone: string): Promise<PagedResponse<IdentityResponse>> =>
      this.request<PagedResponse<IdentityResponse>>('/platform/identities', { query: { phone } }),

    renumber: (userId: number, newPhone: string): Promise<PhoneChangeRequestedResponse> =>
      this.request<PhoneChangeRequestedResponse>(`/platform/identities/${userId}/renumber`, { method: 'POST', body: { newPhone } }),

    release: (userId: number): Promise<NumberReleasedResponse> =>
      this.request<NumberReleasedResponse>(`/platform/identities/${userId}/release`, { method: 'POST', body: {} }),

    /** A one-time link for the person to set their own password; shown once, never texted. */
    recoveryLink: (userId: number): Promise<RecoveryLinkResponse> =>
      this.request<RecoveryLinkResponse>(`/platform/identities/${userId}/recovery-link`, { method: 'POST', body: {} }),
  };

  staff = {
    list: (query: { search?: string; status?: MembershipStatus; classId?: number } = {}):
      Promise<StaffResponse[]> =>
      this.request<StaffResponse[]>('/staff', { query }),

    get: (membershipId: number): Promise<StaffResponse> =>
      this.request<StaffResponse>(`/staff/${membershipId}`),

    invite: (body: InviteStaffRequest): Promise<InvitationIssuedResponse> =>
      this.request<InvitationIssuedResponse>('/staff', { method: 'POST', body }),

    /** A new link for someone who has not accepted yet. Every earlier link stops working. */
    resendInvitation: (membershipId: number): Promise<InvitationIssuedResponse> =>
      this.request<InvitationIssuedResponse>(`/staff/${membershipId}/invitation`, { method: 'POST' }),

    update: (membershipId: number, body: {
      fullName?: string | null; jobTitle?: string | null; roleKey?: string | null;
    }): Promise<void> =>
      this.request<void>(`/staff/${membershipId}`, { method: 'PATCH', body }),

    deactivate: (membershipId: number): Promise<void> =>
      this.request<void>(`/staff/${membershipId}/deactivate`, { method: 'POST' }),

    permissions: (membershipId: number): Promise<PermissionOptionResponse[]> =>
      this.request<PermissionOptionResponse[]>(`/staff/${membershipId}/permissions`),

    setPermissions: (membershipId: number, overrides: PermissionOverrideResponse[]): Promise<void> =>
      this.request<void>(`/staff/${membershipId}/permissions`, { method: 'PUT', body: { overrides } }),

    assignClass: (membershipId: number, body: {
      classId: number; roleInClass: ClassRole; startDate: string | null; endDate: string | null;
    }): Promise<void> =>
      this.request<void>(`/staff/${membershipId}/classes`, { method: 'POST', body }),

    unassignClass: (membershipId: number, classId: number): Promise<void> =>
      this.request<void>(`/staff/${membershipId}/classes/${classId}`, { method: 'DELETE' }),
  };

  guardians = {
    /** The caller's own children — what the guardian app's chooser is built from. */
    myChildren: (): Promise<MyChildResponse[]> => this.request<MyChildResponse[]>('/me/children'),

    /** A holder of children.update gets every standing link in full, and the ended ones with `includeEnded`. */
    forChild: (childId: number, includeEnded = false): Promise<GuardianLinkResponse[]> =>
      this.request<GuardianLinkResponse[]>(
        `/children/${childId}/guardians${includeEnded ? '?includeEnded=true' : ''}`),

    add: (childId: number, body: AddGuardianRequest): Promise<GuardianLinkedResponse> =>
      this.request<GuardianLinkedResponse>(`/children/${childId}/guardians`, { method: 'POST', body }),

    update: (childId: number, linkId: number, body: UpdateGuardianLinkRequest): Promise<void> =>
      this.request<void>(`/children/${childId}/guardians/${linkId}`, { method: 'PATCH', body }),

    /** Ends the link; the record that it existed stays (Q30). */
    remove: (childId: number, linkId: number): Promise<void> =>
      this.request<void>(`/children/${childId}/guardians/${linkId}`, { method: 'DELETE' }),

    /** A new link for a guardian who has not accepted yet. Every earlier link stops working. */
    resendInvitation: (childId: number, linkId: number): Promise<InvitationIssuedResponse> =>
      this.request<InvitationIssuedResponse>(`/children/${childId}/guardians/${linkId}/invitation`, { method: 'POST' }),
  };

  pickups = {
    /** The first page, by name: a child has a handful, so the card reads one page. */
    list: (childId: number): Promise<PagedResponse<AuthorizedPickupResponse>> =>
      this.request<PagedResponse<AuthorizedPickupResponse>>(`/children/${childId}/pickups`),

    add: (childId: number, body: {
      fullName: string; phone: string; relationship: string | null;
      idDocumentRef: string | null; validFrom: string | null; validTo: string | null;
    }): Promise<AuthorizedPickupResponse> =>
      this.request<AuthorizedPickupResponse>(`/children/${childId}/pickups`, { method: 'POST', body }),

    remove: (childId: number, pickupId: number): Promise<void> =>
      this.request<void>(`/children/${childId}/pickups/${pickupId}`, { method: 'DELETE' }),

    /** Who may collect the child today, and who may not: the check-out screen's one call (Q30). */
    collectors: (childId: number): Promise<CollectorsResponse> =>
      this.request<CollectorsResponse>(`/children/${childId}/collectors`),

    restrictions: (childId: number, includeLifted = false): Promise<PagedResponse<PickupRestrictionResponse>> =>
      this.request<PagedResponse<PickupRestrictionResponse>>(
        `/children/${childId}/pickup-restrictions${includeLifted ? '?includeLifted=true' : ''}`),

    addRestriction: (childId: number, body: {
      fullName: string; phone: string | null; note: string; evidenceRef: string | null;
    }): Promise<PickupRestrictionResponse> =>
      this.request<PickupRestrictionResponse>(`/children/${childId}/pickup-restrictions`, { method: 'POST', body }),

    /** Lifts it; the restriction stays on record. */
    liftRestriction: (childId: number, restrictionId: number): Promise<void> =>
      this.request<void>(`/children/${childId}/pickup-restrictions/${restrictionId}`, { method: 'DELETE' }),
  };

  /**
   * A guardian's encrypted copy of a child's records, made and handed over by the administration (Q41, Q51).
   * Needs `children.export`. The password goes by SMS to the guardian alone; no call here ever returns it.
   */
  exports = {
    /** The guardians a copy could be made for, each with the refusal the request itself would give. */
    recipients: (childId: number, query: { page?: number; pageSize?: number } = {}):
      Promise<PagedResponse<ExportRecipientResponse>> =>
      this.request<PagedResponse<ExportRecipientResponse>>(`/children/${childId}/exports/recipients`, { query }),

    list: (childId: number, query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<ChildExportResponse>> =>
      this.request<PagedResponse<ChildExportResponse>>(`/children/${childId}/exports`, { query }),

    create: (childId: number, guardianLinkId: number): Promise<ChildExportResponse> =>
      this.request<ChildExportResponse>(`/children/${childId}/exports`, { method: 'POST', body: { guardianLinkId } }),

    /** The encrypted PDF, to save and hand over; it opens only with the guardian's password. */
    file: (childId: number, exportId: number): Promise<Blob> =>
      this.file(`/children/${childId}/exports/${exportId}/file`, 'application/pdf'),
  };

  /** The record of photos handed to a guardian outside the app (Q41). Needs `children.update`. */
  photoHandovers = {
    list: (childId: number, query: { page?: number; pageSize?: number } = {}): Promise<PagedResponse<PhotoHandoverResponse>> =>
      this.request<PagedResponse<PhotoHandoverResponse>>(`/children/${childId}/photo-handovers`, { query }),

    record: (childId: number, body: {
      guardianLinkId: number; handedOverOn: string | null; note: string | null;
    }): Promise<PhotoHandoverResponse> =>
      this.request<PhotoHandoverResponse>(`/children/${childId}/photo-handovers`, { method: 'POST', body }),
  };

  /**
   * The legal hold that stops the retention purge of a child's records (Q37). Reading needs `children.update`;
   * placing and lifting need `children.legal_hold`, which only the owner can hold.
   */
  legalHold = {
    get: (childId: number): Promise<LegalHoldResponse> =>
      this.request<LegalHoldResponse>(`/children/${childId}/legal-hold`),

    place: (childId: number, reason: string): Promise<LegalHoldResponse> =>
      this.request<LegalHoldResponse>(`/children/${childId}/legal-hold`, { method: 'POST', body: { reason } }),

    lift: (childId: number): Promise<void> =>
      this.request<void>(`/children/${childId}/legal-hold`, { method: 'DELETE' }),
  };

  // ---- Notifications ------------------------------------------------------------------------------------

  notifications = {
    list: (query: { unreadOnly?: boolean; page?: number; pageSize?: number } = {}):
      Promise<PagedResponse<NotificationResponse>> =>
      this.request<PagedResponse<NotificationResponse>>('/notifications', { query }),

    /** For the bell: a number, not a page, because it is asked for every thirty seconds. */
    unreadCount: (): Promise<UnreadCountResponse> =>
      this.request<UnreadCountResponse>('/notifications/unread-count'),

    markRead: (id: number): Promise<void> =>
      this.request<void>(`/notifications/${id}/read`, { method: 'POST' }),

    markAllRead: (): Promise<void> =>
      this.request<void>('/notifications/read-all', { method: 'POST' }),

    preferences: (): Promise<NotificationPreferenceResponse[]> =>
      this.request<NotificationPreferenceResponse[]>('/notifications/preferences'),

    setPreferences: (body: SetPreferenceRequest[]): Promise<void> =>
      this.request<void>('/notifications/preferences', { method: 'PUT', body }),

    registerDevice: (body: RegisterDeviceRequest): Promise<void> =>
      this.request<void>('/notifications/devices', { method: 'POST', body }),

    /** A Web Push endpoint is a URL, which a path cannot carry, so it goes in the body. */
    removeDevice: (token: string): Promise<void> =>
      this.request<void>('/notifications/devices/remove', { method: 'POST', body: { token } }),

    pushConfig: (): Promise<PushConfigResponse> =>
      this.request<PushConfigResponse>('/notifications/push/config'),
  };

  // ---- Admin console ------------------------------------------------------------------------------------

  admin = {
    dashboard: (): Promise<AdminDashboardResponse> =>
      this.request<AdminDashboardResponse>('/admin/dashboard'),

    settings: (): Promise<SettingResponse[]> => this.request<SettingResponse[]>('/admin/settings'),

    /**
     * A null value clears the override and restores the shipped default. Switching review off publishes the days
     * waiting for it, and the answer counts them (C1).
     */
    updateSettings: (values: Record<string, string | null>): Promise<UpdateSettingsResponse> =>
      this.request<UpdateSettingsResponse>('/admin/settings', { method: 'PUT', body: { values } }),

    /** Sets or replaces this nursery's logo. Needs `settings.manage`. */
    setLogo: (logo: Blob): Promise<TenantLogoResponse> =>
      this.sendForm<TenantLogoResponse>('/admin/logo', fileForm(logo, 'logo.png'), 'PUT'),

    removeLogo: (): Promise<void> => this.request<void>('/admin/logo', { method: 'DELETE' }),

    audit: (query: {
      actorUserId?: number; entityTypeId?: number; from?: string; to?: string;
      page?: number; pageSize?: number;
    } = {}): Promise<PagedResponse<AuditEntryResponse>> =>
      this.request<PagedResponse<AuditEntryResponse>>('/admin/audit', { query }),
  };
}

/**
 * An image as the one `file` part the photo and logo endpoints read. The server judges the type by the bytes;
 * the name is for its logs only.
 */
function fileForm(image: Blob, filename: string): FormData {
  const form = new FormData();
  form.append('file', image, filename);
  return form;
}

/** Reads a success body as JSON; an empty body is `undefined`. */
async function readJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/** Reads a problem body, falling back to a bare shape when the response is not JSON (a proxy 502, say). */
async function readProblem(response: Response): Promise<ProblemDetails> {
  try {
    const text = await response.text();
    if (!text) return { status: response.status };
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      return { status: response.status, ...(parsed as ProblemDetails) };
    }
    return { status: response.status, detail: text };
  } catch {
    return { status: response.status };
  }
}

export type { MembershipSummary };
